import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Copy, MagnifyingGlass, Trash, Receipt, Package, Printer } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { useAuth } from "../AuthContext";
import { openPackingSlip } from "../lib/packingSlip";

/**
 * v414.73 — ONE request box for distributors. Pick the request type, search
 * products in a dropdown, add lines, submit:
 *  - restock  -> POST /restock-requests/batch (owner gets one email ping)
 *  - sale     -> POST /sale-requests (memo code; optional fulfil-from-Master)
 * v414.74 — onBehalfOf={id,name}: org roles open the SAME box from a
 * distributor's detail page and file either request type AS that distributor.
 */
export default function UnifiedRequestDialog({ initialType = "restock", onBehalfOf = null, onClose, onDone }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const actorId = onBehalfOf?.id || user.id;
  // partners only ask the org for STOCK — sale requests stay distributor-only
  const restockOnly = onBehalfOf?.role === "partner";
  const [type, setType] = useState(restockOnly ? "restock" : initialType); // restock | sale
  const [fromMaster, setFromMaster] = useState(false); // sale only
  const [catalog, setCatalog] = useState([]); // supplier catalog rows
  const [ownRows, setOwnRows] = useState([]); // own inventory rows
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]); // {row, qty, price}
  const [customer, setCustomer] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // sale success (memo code)

  useEffect(() => {
    // on-behalf: god-mode preview = the catalog EXACTLY as they see it
    const catalogReq = onBehalfOf
      ? api.get(`/distributors/${onBehalfOf.id}/preview`).then(({ data }) => data?.catalog || [])
      : api.get("/restock-requests/catalog").then(({ data }) => data || []);
    catalogReq
      .then((rows) => setCatalog(rows))
      .catch((e) => toast.error(formatApiError(e)));
    api.get(`/distributors/${actorId}/inventory`)
      .then(({ data }) => {
        const rows = (data || []).filter((r) => (r.current_stock || 0) > 0);
        setOwnRows(rows);
        if (rows.length === 0 && initialType === "sale" && !restockOnly) setFromMaster(true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // active source rows, normalised to {id, name, unit, sub, ws, maxQty}
  const source = useMemo(() => {
    if (type === "sale" && !fromMaster) {
      return ownRows.map((r) => ({
        id: r.id, name: r.name, unit: r.unit || "vials",
        sub: `${r.current_stock} ${r.unit || "vials"}`,
        ws: r.wholesale_price != null ? Number(r.wholesale_price) : null,
        maxQty: r.current_stock,
      }));
    }
    // RESTOCK is a request — out-of-stock items stay visible and requestable
    // (the supplier orders them in; the old SupplierCatalog worked this way).
    // Master-fulfilled SALES still need the supplier to actually hold stock.
    return catalog
      .filter((r) => !r.coming_soon && (type === "restock" || r.available))
      .sort((a, b) => (b.available ? 1 : 0) - (a.available ? 1 : 0))
      .map((r) => ({
        id: r.id, name: r.name, unit: r.unit || "vials",
        sub: r.made_to_order
          ? t("unified_request.made_to_order")
          : r.available ? t("unified_request.in_stock") : t("unified_request.out_of_stock"),
        ws: r.ws != null ? Number(r.ws) : null,
        maxQty: null,
        already_held: r.already_held, held_product_id: r.held_product_id,
      }));
  }, [type, fromMaster, catalog, ownRows, t]);

  const results = useMemo(() => {
    const used = new Set(lines.map((l) => l.row.id));
    const q = search.trim().toLowerCase();
    return source
      .filter((r) => !used.has(r.id) && (!q || (r.name || "").toLowerCase().includes(q)))
      .slice(0, 8);
  }, [search, source, lines]);

  const addLine = (row) => {
    setLines((ls) => [...ls, { row, qty: 1, price: row.ws != null ? String(row.ws) : "" }]);
    setSearch("");
  };

  const switchType = (nextType, nextFromMaster = fromMaster) => {
    setType(nextType);
    // nothing on hand -> selling from own stock is a dead end; default to
    // Master fulfilment so the search is never silently empty (v414.79)
    const master = nextType === "sale"
      ? (ownRows.length === 0 ? true : nextFromMaster)
      : false;
    setFromMaster(master);
    setLines([]); // sources differ — start the item list clean
  };

  const total = lines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.qty) || 0), 0);

  const submit = async () => {
    if (lines.length === 0) {
      toast.error(t("unified_request.need_items"));
      return;
    }
    setBusy(true);
    try {
      if (type === "restock") {
        const items = lines.map((l) => (
          l.row.already_held && l.row.held_product_id
            ? { product_id: l.row.held_product_id, quantity: Number(l.qty) || 1 }
            : { supplier_product_id: l.row.id, quantity: Number(l.qty) || 1 }
        ));
        await api.post("/restock-requests/batch", {
          items,
          note: note.trim() || "",
          on_behalf_of_id: onBehalfOf?.id || undefined,
        });
        toast.success(t("unified_request.restock_sent", { n: lines.length }));
        onDone?.();
        onClose();
      } else {
        const { data } = await api.post("/sale-requests", {
          items: lines.map((l) => ({
            product_id: l.row.id,
            quantity: Number(l.qty) || 1,
            unit_price: l.price !== "" ? Number(l.price) : undefined,
          })),
          customer_name: customer.trim() || undefined,
          note: note.trim() || undefined,
          fulfill_from_master: fromMaster || undefined,
          on_behalf_of_id: onBehalfOf?.id || undefined,
        });
        setCreated(data);
        onDone?.();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-5 w-full max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="unified-request-dialog"
      >
        {created ? (
          <div className="text-center space-y-4 py-4" data-testid="unified-request-success">
            <p className="text-sm text-zinc-600">{t("sale_request.memo_instruction")}</p>
            <p className="text-3xl font-bold font-mono tracking-widest text-zinc-950" data-testid="unified-request-code">{created.code}</p>
            <div className="flex gap-2">
              <BrutalButton variant="secondary" className="flex-1" onClick={() => {
                navigator.clipboard?.writeText(created.code);
                toast.success(t("common.copied"));
              }} data-testid="unified-request-copy">
                <Copy size={15} weight="bold" /> {t("common.copy")}
              </BrutalButton>
              <BrutalButton variant="secondary" className="flex-1" onClick={() => openPackingSlip(created)} data-testid="unified-request-slip">
                <Printer size={15} weight="bold" /> {t("sale_request.packing_slip")}
              </BrutalButton>
            </div>
            <BrutalButton className="w-full" onClick={onClose} data-testid="unified-request-done">
              {t("common.done")}
            </BrutalButton>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold">
                {onBehalfOf
                  ? t("unified_request.title_on_behalf", { name: onBehalfOf.name })
                  : t("unified_request.title")}
              </h2>
              <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="unified-request-close">
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">{t("unified_request.subtitle")}</p>

            {/* type selector */}
            <div className={`grid gap-2 mb-3 ${restockOnly ? "grid-cols-1" : "grid-cols-2"}`}>
              <button
                type="button"
                onClick={() => switchType("restock")}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${type === "restock" ? "border-[#c9a24a] bg-[#fbf6e7]" : "border-zinc-200 hover:border-zinc-300"}`}
                data-testid="unified-type-restock"
              >
                <Package size={18} weight="bold" className="text-[#8a6a1c]" />
                <p className="text-sm font-bold mt-1">{t("unified_request.type_restock")}</p>
                <p className="text-[11px] text-zinc-500">{t("unified_request.type_restock_hint")}</p>
              </button>
              {!restockOnly && (
              <button
                type="button"
                onClick={() => switchType("sale")}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${type === "sale" ? "border-[#c9a24a] bg-[#fbf6e7]" : "border-zinc-200 hover:border-zinc-300"}`}
                data-testid="unified-type-sale"
              >
                <Receipt size={18} weight="bold" className="text-[#8a6a1c]" />
                <p className="text-sm font-bold mt-1">{t("unified_request.type_sale")}</p>
                <p className="text-[11px] text-zinc-500">{t("unified_request.type_sale_hint")}</p>
              </button>
              )}
            </div>

            {type === "sale" && (
              <label className="flex items-start gap-2 cursor-pointer select-none rounded-lg border border-zinc-200 p-2.5 bg-zinc-50 mb-3" data-testid="unified-from-master-row">
                <input type="checkbox" checked={fromMaster}
                  onChange={(e) => switchType("sale", e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#c9a24a]" data-testid="unified-from-master" />
                <span className="text-xs">
                  <span className="font-semibold">{t("sale_request.fulfill_master_label")}</span>
                  <span className="block text-zinc-500 mt-0.5">{t("unified_request.from_master_hint")}</span>
                </span>
              </label>
            )}

            {/* product search */}
            <div className="relative mb-2">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <BrutalInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("unified_request.search_ph")}
                className="pl-9"
                data-testid="unified-request-search"
              />
              {(search || lines.length === 0) && results.length > 0 && (
                <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="unified-request-results">
                  {results.map((r) => (
                    <button key={r.id} type="button" onClick={() => addLine(r)}
                      className="w-full text-left px-3 py-2 hover:bg-[#fbf6e7] border-b border-zinc-100 last:border-0"
                      data-testid={`unified-result-${r.id}`}>
                      <p className="text-sm font-semibold">{r.name}</p>
                      <p className="text-[11px] text-zinc-500">
                        {r.sub}{r.ws != null ? ` · WS $${r.ws.toFixed(2)}` : ""}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* lines */}
            <div className="space-y-2 mb-3">
              {lines.map((l, idx) => (
                <div key={l.row.id} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2" data-testid={`unified-line-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{l.row.name}</p>
                    <p className="text-[11px] text-zinc-500">{l.row.sub}</p>
                  </div>
                  <input type="number" min="1" max={l.row.maxQty ?? undefined} value={l.qty}
                    onChange={(e) => setLines((ls) => ls.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))}
                    className="w-16 h-9 border border-zinc-300 rounded-md px-2 text-sm text-center"
                    data-testid={`unified-qty-${idx}`} />
                  {type === "sale" && (
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">$</span>
                      <input type="number" min="0" step="0.01" value={l.price}
                        onChange={(e) => setLines((ls) => ls.map((x, i) => (i === idx ? { ...x, price: e.target.value } : x)))}
                        className="w-24 h-9 border border-zinc-300 rounded-md pl-5 pr-2 text-sm"
                        data-testid={`unified-price-${idx}`} />
                    </div>
                  )}
                  <button type="button" onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                    className="text-zinc-400 hover:text-red-600 p-1" data-testid={`unified-remove-${idx}`}>
                    <Trash size={15} />
                  </button>
                </div>
              ))}
              {lines.length === 0 && (
                <p className="text-xs text-zinc-400 text-center py-2" data-testid="unified-empty-hint">
                  {type === "sale" && !fromMaster && ownRows.length === 0
                    ? t("unified_request.no_own_stock_hint")
                    : t("unified_request.empty_hint")}
                </p>
              )}
            </div>

            {type === "sale" && (
              <BrutalInput value={customer} onChange={(e) => setCustomer(e.target.value)}
                placeholder={t("sale_request.customer_placeholder")} className="mb-2"
                data-testid="unified-request-customer" />
            )}
            <BrutalInput value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={t("unified_request.note_ph")} className="mb-3"
              data-testid="unified-request-note" />

            {type === "sale" && lines.length > 0 && (
              <div className="flex justify-between text-sm font-semibold mb-3" data-testid="unified-request-total">
                <span>{t("sale_request.total")}</span>
                <span className="tabular-nums">${total.toFixed(2)}</span>
              </div>
            )}

            <BrutalButton className="w-full" disabled={busy || lines.length === 0} onClick={submit} data-testid="unified-request-submit">
              {busy
                ? t("assign.sending")
                : type === "restock"
                  ? t("unified_request.submit_restock")
                  : t("sale_request.generate_code")}
            </BrutalButton>
          </>
        )}
      </div>
    </div>
  );
}
